import createCRUDObservable from 'redux-observable-crud'
import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs'
import { clone, findIndex, isEmpty, propEq } from 'ramda'
import ClassroomActions, { ClassroomRedux, ClassroomTypes, Selectors } from '../Redux/ClassroomRedux'
import CompaniesActions, { CompaniesRedux, CompaniesTypes } from '../Redux/CompaniesRedux'
import CenterProductsActions, { CenterProductsRedux, CenterProductsTypes } from '../Redux/CenterProductsRedux'
import { notificationSuccess, notificationFail } from '../Redux/NotificationActions'
import StudyProgramsActions from '../Redux/StudyProgramsRedux'
import ParentPaymentTypesActions from '../Redux/ParentPaymentTypesRedux'

const classroomCrudObservable = createCRUDObservable({
    mainRedux: ClassroomRedux,
    reduxPath: 'classrooms',
    dataHandlers: {
        get: response => response.data.data
            .sort((a, b) => {
                const pattName = new RegExp(/[Ss][ala]+\s[0-9]+/)
                const pattNumber = new RegExp(/[0-9]*/)
                const nameA = a.name.match(pattName) || []
                const nameB = b.name.match(pattName) || []
                    // sort by classroom number
                if (!isEmpty(nameA) && !isEmpty(nameB)) {
                    const [numberA] = nameA[0].match(pattNumber)
                    const [numberB] = nameB[0].match(pattNumber)
                    return numberA - numberB;
                }

                // sort by classroom name
                if (a.name > b.name) {
                    return 1
                }

                if (a.name < b.name) {
                    return -1
                }

                return 0
            }),
    },
})

const getStudentProfileEpic = (action$, store, { Api }) => (
    action$.ofType(ClassroomTypes.getStudentProfileRequest)
    .mergeMap(({ activeStudentId, timestamp, isCheckedOut }) => (
        Api.classrooms.getStudentProfile(activeStudentId, timestamp)
        .then(response =>
            ClassroomActions.getStudentProfileSuccess(response.data.data, isCheckedOut))
        .catch(err => ClassroomActions.getStudentProfileError(err))
    ))
)

const getParentProfileEpic = (action$, store, { Api }) => (
    action$.ofType(ClassroomTypes.getParentProfileRequest)
    .mergeMap(({ activeParentId, activeStudentId, timestamp }) => (
        Api.classrooms.getParentProfile(activeParentId, activeStudentId, timestamp)
        .then(response => ClassroomActions.getParentProfileSuccess(response.data.data))
        .catch(err => ClassroomActions.getParentProfileError(err))
    ))
)

const getStudentActivitiesEpic = (action$, store, { Api }) => (
    action$.ofType(ClassroomTypes.getStudentActivitiesRequest)
    .mergeMap(({ studentId }) => (
        Observable
        .fromPromise(Api.studentProfile.getActivities(studentId)
            .then(response => response)
            .catch(err => err))
        .flatMap((response) => {
            const { status } = response

            if (!(status >= 200 && status < 300)) {
                return [ClassroomActions.getStudentActivitiesError(response.error)]
            }

            const { data: { data } } = response

            const state = store.getState()
            const {
                activeClassroomIndex,
                activeStudentIndex,
                classrooms: newClassrooms,
                student,
            } = Selectors.activeStudentFromLocal(state.classrooms)

            if (student) {
                student.latest_activities.push(data)
            }

            if (activeStudentIndex >= 0) {
                if (newClassrooms[activeClassroomIndex]) {
                    newClassrooms[activeClassroomIndex].students[activeStudentIndex] = student
                }
            } else {
                newClassrooms[activeClassroomIndex].students.push(student)
            }

            return [
                ClassroomActions.getStudentActivitiesSuccess(newClassrooms),
            ]
        })
    ))
)
const getCenterEpic = (action$, store, { Api }) => (
    action$.ofType(ClassroomTypes.getCenterRequest)
    .mergeMap(({ classroomId }) => (
        Observable
        .fromPromise(Api.centerClassroom.getCenterOfClassroom(classroomId)
            .then(response => response)
            .catch(err => err))
        .flatMap((response) => {
            const { status } = response
            if (!(status >= 200 && status < 300)) {
                return [
                    ClassroomActions.getCenterError(response.error),
                ]
            }
            return [
                ClassroomActions.getClassroomRequest(response.data.data.id),
                CompaniesActions.getRequest(),
                CenterProductsActions.getRequest(response.data.data.id),
                StudyProgramsActions.getRequest(response.data.data.id),
                ParentPaymentTypesActions.getParentPaymentRequest(response.data.data.id),
                ClassroomActions.getCenterSuccess(response.data.data),
            ]
        })
    ))
)
const sendMailInstitutionalEpic = (action$, store, { Api }) => (
    action$.ofType(ClassroomTypes.sendMailInstitutionalRequest)
    .mergeMap(({ activeParentId, activeStudentId }) => (
        Observable
        .fromPromise(Api.classrooms.sendMailInstitutional(activeParentId, activeStudentId)
            .then(response => response)
            .catch(err => err))
        .flatMap((response) => {
            const { status } = response
            if (!(status >= 200 && status < 300)) {
                return [
                    ClassroomActions.sendMailInstitutionalError(response.error),
                ]
            }
            return [
                ClassroomActions.sendMailInstitutionalSuccess(),
            ]
        })
    ))
)
const getClassroomsEpic = (action$, store, { Api }) => (
    action$.ofType(ClassroomTypes.getClassroomRequest)
    .mergeMap(({ centerId }) => (
        Observable
        .fromPromise(Api.centerClassroom.get(centerId)
            .then(response => response)
            .catch(err => err))
        .flatMap((response) => {
            const { status } = response
            if (!(status >= 200 && status < 300)) {
                return [
                    ClassroomActions.getClassroomError(response.error),
                ]
            }
            return [
                ClassroomActions.getClassroomSuccess(response.data.data),
            ]
        })
    ))
)
const updateStudentEpic = (action$, store, { Api }) => (
    action$.ofType(ClassroomTypes.updateStudentRequest)
    .mergeMap(({ activeStudentId, profile, timestamp }) => (
        Observable
        .fromPromise(Api.classrooms
            .updateStudent(activeStudentId, profile, timestamp)
            .then(response => response)
            .catch(err => err))
        .flatMap((response) => {
            const { status } = response

            if (!(status >= 200 && status < 300)) {
                return [ClassroomActions.getStudentProfileError(response.error),
                    notificationFail(`Proceso fallido (${response.error})`),
                ]
            }

            const { data: { data } } = response

            if (profile.classroom_id) {
                return [
                    ClassroomActions.updateStudentSuccess(data),
                    ClassroomActions.setActiveClassroomId(profile.classroom_id),
                    notificationSuccess(`Actualización exitosa (${data.first_name})`),
                    setTimeout(() => {
                        window.location.reload();
                    }, 3000)
                ]
            }

            return [
                ClassroomActions.updateStudentSuccess(data),
                notificationSuccess(`Actualización exitosa (${data.first_name})`),
            ]
        })
    ))
)

const updateParentEpic = (action$, store, { Api }) => (
    action$.ofType(ClassroomTypes.updateParentRequest)
    .mergeMap(({ activeParentId, activeStudentId, profile }) => (
        Observable
        .fromPromise(Api.classrooms
            .updateParent(activeParentId, activeStudentId, profile)
            .then(response => response)
            .catch(err => err))
        .flatMap((response) => {
            const { status } = response

            if (!(status >= 200 && status < 300)) {
                return [ClassroomActions.getParentProfileError(response.error)]
            }

            const { data: { data } } = response

            return [
                ClassroomActions.updateParentSuccess(data),
                notificationSuccess(`Actualización exitosa (${data.first_name})`),
            ]
        })
    ))
)

const deleteStudentEpic = (action$, store, { Api }) => (
    action$.ofType(ClassroomTypes.deleteStudentRequest)
    .mergeMap(({ studentId, deleteInfo }) => (
        Observable
        .fromPromise(Api.classrooms.deleteStudent(studentId, deleteInfo)
            .then(response => response)
            .catch(err => err))
        .flatMap((response) => {
            const { status } = response

            if (!(status >= 200 && status < 300)) {
                return [
                    ClassroomActions.deleteStudentError(response.error),
                    notificationFail('Proceso fallido'),
                ]
            }

            return [
                ClassroomActions.deleteStudentSuccess(response),
                notificationSuccess('Proceso exitoso'),
            ]
        })
    ))
)

const restoreStudentEpic = (action$, store, { Api }) => (
    action$.ofType(ClassroomTypes.restoreStudentRequest)
    .mergeMap(({ id, deleteInfo }) => (
        Observable
        .fromPromise(Api.classrooms.restoreStudent(id, deleteInfo)
            .then(response => response)
            .catch(err => err))
        .flatMap((response) => {
            const { status } = response
            if (!(status >= 200 && status < 300)) {
                return [
                    ClassroomActions.restoreStudentError(response.error),
                    notificationFail('Proceso fallido'),
                ]
            }
            return [
                ClassroomActions.restoreStudentSuccess(response),
                notificationSuccess('Proceso exitoso'),
            ]
        })
    ))
)

const getTotalStudentsEpic = action$ => (
    action$.ofType(ClassroomTypes.getSuccess)
    .flatMap(data => ([
        ClassroomActions.getTotalStudents(data),
    ]))
)

const getStudentInvoicesEpic = (action$, store, { Api }) => (
    action$.ofType(ClassroomTypes.getStudentInvoicesRequest)
    .mergeMap(({ id, queries }) => (
        Observable
        .fromPromise(Api.classrooms.getInvoices(id, queries)
            .then(response => response)
            .catch(err => err))
        .flatMap((response) => {
            const { status } = response
            if (!(status >= 200 && status < 300)) {
                return [
                    ClassroomActions.getStudentInvoicesError(response.error),
                ]
            }

            return [ClassroomActions.getStudentInvoicesSuccess(response.data)]
        })
    ))
)

const createParentEpic = (action$, store, { Api }) => (
    action$.ofType(ClassroomTypes.createParentRequest)
    .mergeMap(({ studentId, info }) => (
        Observable
        .fromPromise(Api.classrooms.createParent(studentId, info)
            .then(response => response)
            .catch(err => err))
        .flatMap((response) => {
            const { status } = response
            if (!(status >= 200 && status < 300)) {
                return [
                    ClassroomActions.createParentError(response.error),
                    notificationFail(response.error),
                ]
            }

            return [
                ClassroomActions.createParentSuccess(response.data),
                notificationSuccess('Se ha registrado al tutor, exitosamente.'),
            ]
        })
    ))
)

export const classroomObservers = Object.assign({}, classroomCrudObservable.observers, {})

export const classroomEpic = combineEpics(
    classroomCrudObservable.epic,
    getStudentProfileEpic,
    getStudentActivitiesEpic,
    getCenterEpic,
    getClassroomsEpic,
    getParentProfileEpic,
    updateStudentEpic,
    updateParentEpic,
    deleteStudentEpic,
    restoreStudentEpic,
    getTotalStudentsEpic,
    getStudentInvoicesEpic,
    createParentEpic,
    sendMailInstitutionalEpic,
)


// WEBPACK FOOTER //
// ./src/Data/Observables/ClassroomObservable.js